import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { globalTheme } from '@cld/component-theme';
import { website } from '@cld/typography';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { getAssetUrl } from '../../../../utils';
import { messages } from '../../../../i18n';
import { View } from './View';
import { Tab } from './Tab';

const Root = styled.div`
  padding: calc(${globalTheme.spacing.lg} * 2) calc(${globalTheme.spacing.lg} * 10);
  margin: auto;
  max-width: calc(${globalTheme.spacing.lg} * 68);
`;

const Heading = styled.h2`
  text-align: center;
  ${website.headlines.xl.bold};
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;

  & > * {
    margin: ${globalTheme.spacing.sm} ${globalTheme.spacing.xxs};
  }
`;

enum TabId {
  Manage = 'manage',
  Transform = 'transform',
  Customize = 'customize',
  Authenticate = 'authenticate',
}

export const Tabs: FC = () => {
  const [activeTab, setActiveTab] = useState(TabId.Manage);
  const { formatMessage } = useIntl();
  const analytics = useAnalytics();

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId);
    analytics('click', `clicked on ${tabId}`);
  };

  return (
    <Root>
      <Heading>
        <FormattedMessage {...messages['landing.tabsSection.heading']} />
      </Heading>

      <TabsContainer data-test="tabs-section-tabs-container">
        <Tab id={TabId.Manage} selected={TabId.Manage === activeTab} onClick={() => handleTabClick(TabId.Manage)}>
          <FormattedMessage {...messages['landing.tabsSection.tabs.labels.manage']} />
        </Tab>
        <Tab id={TabId.Transform} selected={TabId.Transform === activeTab} onClick={() => handleTabClick(TabId.Transform)}>
          <FormattedMessage {...messages['landing.tabsSection.tabs.labels.transform']} />
        </Tab>
        <Tab id={TabId.Customize} selected={TabId.Customize === activeTab} onClick={() => handleTabClick(TabId.Customize)}>
          <FormattedMessage {...messages['landing.tabsSection.tabs.labels.customize']} />
        </Tab>
        <Tab id={TabId.Authenticate} selected={TabId.Authenticate === activeTab} onClick={() => handleTabClick(TabId.Authenticate)}>
          <FormattedMessage {...messages['landing.tabsSection.tabs.labels.authenticate']} />
        </Tab>
      </TabsContainer>

      {activeTab === TabId.Manage && (
        <View
          tab={TabId.Manage}
          text={formatMessage(messages['landing.tabsSection.tabs.texts.manage'])}
          img={getAssetUrl('tabs/manage.png')}
          link={'https://cloudinary.com/documentation/dam_digital_asset_management'}
        />
      )}
      {activeTab === TabId.Transform && (
        <View
          tab={TabId.Transform}
          text={formatMessage(messages['landing.tabsSection.tabs.texts.transform'])}
          img={getAssetUrl('tabs/transform.png')}
          link={'https://cloudinary.com/documentation/image_transformations'}
        />
      )}
      {activeTab === TabId.Customize && (
        <View
          tab={TabId.Customize}
          text={formatMessage(messages['landing.tabsSection.tabs.texts.customize'])}
          img={getAssetUrl('tabs/customize.png')}
          link={'https://cloudinary.com/documentation/media_library_widget#3_set_the_configuration_options'}
        />
      )}
      {activeTab === TabId.Authenticate && (
        <View
          tab={TabId.Authenticate}
          text={formatMessage(messages['landing.tabsSection.tabs.texts.authenticate'])}
          img={getAssetUrl('tabs/authenticate.png')}
          link={'https://cloudinary.com/documentation/media_library_widget#2_optional_generate_the_authentication_signature'}
        />
      )}
    </Root>
  );
};
